import type { AppToolPresentation } from '@web/api/types'

type ToolDisplayNameInput = {
  identifier?: string | null
  title?: string | null
  presentation?: AppToolPresentation
}

/** Choose an authored tool label before deriving one from an opaque identifier. */
export function toolDisplayName({
  identifier,
  title,
  presentation,
}: ToolDisplayNameInput): string {
  const explicit = title?.trim()
  if (explicit && !isOpaqueToolName(explicit) && explicit !== 'Agent tool')
    return cleanToolLabel(explicit)
  if (presentation?.title) return cleanToolLabel(presentation.title)
  return cleanToolLabel(humanizeToolName(identifier ?? explicit))
}

/** Keep Factory implementation names out of the human-facing tool label. */
function cleanToolLabel(value: string): string {
  return value.replace(
    /\bFactory\s+(?:Canned|Cached)\s+(Plan|Seed)\b/gi,
    (_match, action: string) => `Factory ${action}`,
  )
}

/** Present opaque MCP tool identifiers without hiding their source or action. */
export function humanizeToolName(name: string | undefined): string {
  if (!name) return 'Tool'
  const match = /^mcp__([^_].*?)__(.+)$/.exec(name)
  if (!match) return hasSeparator(name) ? titleCase(name) : name
  const server = match[1] ?? ''
  const action = match[2] ?? ''
  const prefix = new RegExp(`^${escapeRegExp(server)}[_-]+`)
  const readableAction = action.replace(prefix, '')
  // App-backend is an implementation detail; its action is the useful label.
  if (server === 'app-backend' || server === 'app_backend')
    return titleCase(readableAction)
  return [titleCase(server), titleCase(readableAction)].join(' · ')
}

function isOpaqueToolName(value: string): boolean {
  return value.startsWith('mcp__')
}

function hasSeparator(value: string): boolean {
  return /[_./-]/.test(value)
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function titleCase(value: string): string {
  return value
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_./-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())
}
